<script setup lang="ts">
import { HgButton } from '@smg-real-estate/domus';
import type { GeoLocation, GeoSearchResultItem, Locale } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationRaw,
  useRouter,
} from 'vue-router';

import AgencyFocus from '@/components/AgencyFocus/AgencyFocus.vue';
import Locations from '@/components/Locations/Locations.vue';
import { ValidationStates } from '@/components/Locations/validationStates';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { useGeoStore } from '@/stores/geo';
import { LoadState } from '@/stores/types';
import { trackSearchButtonSubmit } from '@/tracking/tracking';

const { t } = useI18n();
const geoSearchResultList = ref<GeoSearchResultItem[]>([]);
const agencyProfilesStore = useAgencyProfilesStore();
const geoStore = useGeoStore();
const { agenciesLoadState, agenciesCountLoadState, selectedLocations, selectedListingType } =
  storeToRefs(agencyProfilesStore);
const router = useRouter();
const locale = useLocale();

const handleSearchTextChange = async (searchText: string) => {
  if (searchText.length < 2) {
    return;
  }

  await geoStore.autocompleteLocations({
    query: searchText,
    language: locale.value,
  });

  const locations = geoStore.getAutocompleteResults;

  if (!locations) {
    return;
  }

  geoSearchResultList.value = locations.filter((item) => item.geoLocation.type != 'ziparea');
};

const handleLocationChange = (locations: GeoLocation[]) => {
  agencyProfilesStore.changeSelectedLocations(locations);
  agencyProfilesStore.loadAgenciesCount();
};

const handleSearchAgencies = () => {
  trackSearchButtonSubmit({
    selectedLocations: selectedLocations.value,
    listingType: selectedListingType.value,
  });
  router
    .push(
      agencyProfilesStore.nextPageRoute(
        1,
        locale.value as unknown as Locale,
      ) as unknown as RouteLocationRaw,
    )
    .catch((error) => {
      // Ignore duplicated navigation errors
      if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
        throw Error(error);
      }
    });
};

const searchButtonText = computed(() => {
  return agencyProfilesStore.agenciesLoadState === LoadState.LOADED ||
    agencyProfilesStore.agenciesCountLoadState === LoadState.LOADED
    ? `${agencyProfilesStore.totalFilteredAgenciesCount} ${t('searchBox.resultButtonText')}`
    : t('searchBox.buttonText');
});

watch(selectedListingType, async () => {
  await agencyProfilesStore.loadAgenciesCount();
});
</script>

<template>
  <div>
    <div :class="$style.searchBarWrapper">
      <Locations
        data-dd-action-name="SearchBar Locations Input"
        :t="t"
        :locale="locale"
        :searchResults="geoSearchResultList"
        :selectedLocations="agencyProfilesStore.selectedLocations"
        :validationState="ValidationStates.Valid"
        @locationsChanged="handleLocationChange"
        @searchTextUpdated="handleSearchTextChange"
      />
      <HgButton
        data-dd-action-name="SearchBar Search Button"
        data-test="search-button"
        iconPosition="left"
        :icon="
          agenciesLoadState === LoadState.LOADING || agenciesCountLoadState === LoadState.LOADING
            ? 'SpinnerHalf'
            : 'Search'
        "
        :class="$style.searchButton"
        @click="handleSearchAgencies"
        >{{ searchButtonText }}
      </HgButton>
      <div :class="$style.focusFilterWrapper">
        <AgencyFocus />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.searchIcon {
  position: absolute;
  top: 40px;
  left: 12px;

  @include color-icon($color-grey-80);
}

.searchBarWrapper {
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-auto-flow: column dense;
  column-gap: $space-150;
  padding: 16px;
  margin-top: $space-150;
  background-color: $color-white;
  border-radius: $border-radius-l;

  @media (max-width: $viewport-sm-min) {
    grid-template-rows: 1fr;
    grid-template-columns: unset;
    grid-auto-flow: row;
    row-gap: $space-100;
  }

  .searchButton {
    grid-row-start: 1;
    margin-top: 30px;
    text-align: center;

    @media (max-width: $viewport-sm-min) {
      grid-row-start: 3;
      margin-top: 0;
    }
  }

  .focusFilterWrapper {
    grid-row-start: 3;
    padding-top: $space-150;

    @media (max-width: $viewport-sm-min) {
      grid-row-start: 2;
    }
  }
}
</style>
